import { MapPin, Navigation, Copy, ExternalLink, Network } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";

const TURIN =
  "https://images.pexels.com/photos/35103839/pexels-photo-35103839.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ADDRESS = "Corso Trapani 150, 10141 Torino (TO)";

export const Studio = () => {
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(ADDRESS);
      toast.success("Indirizzo copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare l'indirizzo");
    }
  };

  return (
    <section id="studio" data-testid="studio-section" className="py-24 sm:py-32 lg:py-40 bg-ink-soft/40 border-y border-gold/10">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <Reveal>
          <Overline>Il centro e lo studio</Overline>
          <h2 className="font-serif tracking-tight leading-snug text-3xl sm:text-4xl lg:text-5xl text-sand max-w-3xl">
            Un luogo dove <span className="italic text-gold">fermarsi</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="forbas-banner"
            className="mt-12 rounded-2xl border border-terracotta/40 bg-gradient-to-r from-terracotta/15 via-ink-elevated to-ink-elevated p-8 sm:p-12 flex flex-col sm:flex-row items-start gap-8"
          >
            <div className="w-14 h-14 shrink-0 rounded-full border border-terracotta/50 flex items-center justify-center">
              <Network size={24} className="text-terracotta" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta font-semibold">
                Centro Clinico Psicoanalitico
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-sand mt-3">Forbas — Torino</h3>
              <p className="mt-4 text-base text-sand-muted leading-relaxed max-w-2xl">
                Svolgo la mia attività clinica anche all'interno di Forbas, centro clinico
                psicoanalitico di Torino: una realtà di confronto e collaborazione tra professionisti,
                dove il lavoro con la persona è sostenuto da una rete clinica condivisa e da una
                solida tradizione psicoanalitica.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="relative rounded-2xl overflow-hidden h-full min-h-[380px] group">
              <img
                src={TURIN}
                alt="Panorama di Torino — zona dello studio di psicologia in Corso Trapani"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover grayscale-[30%] group-hover:scale-105 transition-transform duration-[1.2s]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/20" />
              <div className="relative p-8 sm:p-12 flex flex-col justify-end h-full min-h-[380px]">
                <div className="flex items-center gap-3 text-gold">
                  <MapPin size={22} />
                  <p className="text-xs uppercase tracking-[0.3em] font-semibold">Lo studio</p>
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl text-sand mt-4" data-testid="studio-address">
                  Corso Trapani 150
                </h3>
                <p className="text-sand-muted mt-2">10141 Torino (TO)</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    data-testid="location-maps-link"
                    href="https://www.google.com/maps/search/?api=1&query=45.0648189,7.6363222"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-ink bg-gold hover:bg-sand px-6 py-3 rounded-full transition-colors duration-300"
                  >
                    Apri in Google Maps <ExternalLink size={14} />
                  </a>
                  <button
                    data-testid="location-copy-address-button"
                    onClick={copyAddress}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-sand border border-gold/40 hover:border-gold px-6 py-3 rounded-full transition-colors duration-300 hover:bg-gold/10"
                  >
                    Copia indirizzo <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <Reveal delay={0.15}>
              <div className="space-y-4" data-testid="studio-gallery">
                <div className="relative rounded-2xl overflow-hidden h-52 group">
                  <img
                    src="/studio-scrivania.jpg"
                    alt="Lo studio del Dott. Moretti in Corso Trapani 150 — area colloquio con poltrone"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]"
                    data-testid="studio-interior-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                  <p className="absolute bottom-4 left-5 text-xs uppercase tracking-[0.25em] text-sand/90">
                    Lo studio — Corso Trapani 150
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative rounded-xl overflow-hidden h-32 group">
                    <img
                      src="/studio-poltrona.jpg"
                      alt="Angolo d'ascolto dello studio — poltrona e libreria"
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]"
                      data-testid="studio-gallery-image-2"
                    />
                  </div>
                  <div className="relative rounded-xl overflow-hidden h-32 group">
                    <img
                      src="/studio-sala.jpg"
                      alt="La scrivania dello studio, tra libri e piante"
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]"
                      data-testid="studio-gallery-image-3"
                    />
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="bg-ink-elevated border border-gold/15 rounded-2xl p-8">
                <h4 className="font-sans font-medium text-lg text-sand">Come raggiungermi</h4>
                <p className="mt-5 text-sm text-sand-muted leading-relaxed">
                  Il mio studio si trova in Corso Trapani 150, Torino, in una zona facilmente
                  raggiungibile sia in auto sia con i mezzi pubblici.
                </p>
                <p className="mt-4 text-sm text-sand-muted leading-relaxed">
                  Per organizzare al meglio la tua visita, ti consiglio di fissare un appuntamento
                  in anticipo.
                </p>
                <ul className="mt-6 space-y-4">
                  <li className="flex items-start gap-3 text-sm">
                    <MapPin size={18} className="text-gold shrink-0 mt-0.5" />
                    <span className="text-sand-muted">
                      <span className="text-sand">Indirizzo:</span> Corso Trapani 150, Torino
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <Navigation size={18} className="text-gold shrink-0 mt-0.5" />
                    <span className="text-sand-muted">
                      <span className="text-sand">Indicazioni stradali:</span>{" "}
                      <a
                        data-testid="directions-maps-link"
                        href="https://www.google.com/maps/search/?api=1&query=45.0648189,7.6363222"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold gold-underline pb-0.5 hover:text-sand transition-colors duration-300"
                      >
                        Apri Google Maps
                      </a>
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
